import React, { useCallback } from 'react';
import { push } from 'connected-react-router';
import { useRouteMatch } from 'react-router';
import { useDispatch, useSelector } from 'react-redux';

import { useGetPromotersQuery } from 'api/main';
import { userSelectors } from 'reducers';
import { useLocalStorageFilters } from 'utils/hooks';
import { ORDER_BY, listingActionButtonStyle, sortDirection } from 'utils/constants';
import { texts } from 'utils/languages/fr';

import PromoterRow from './PromoterRow';
import { ActionButton, CustomTablePagination, EmptyTableBody, EnhancedTableHead } from 'components';
import {
    Box,
    Table,
    TableBody,
    TableContainer,
    TableFooter,
    TableRow,
    Typography,
} from '@mui/material';

const tableHeads = [
    { id: ORDER_BY.name, width: 100, isSortable: true, label: texts.PROMOTER_NAME },
    { id: 'actions', width: 5, isSortable: false, label: '' },
];

const getDefaultFilters = () => ({
    page: 1,
    orderBy: ORDER_BY.name,
    direction: sortDirection.ASC,
    itemsPerPage: 15,
});

const PromotersTable = () => {
    const { url } = useRouteMatch();
    const dispatch = useDispatch();
    const internalUser = useSelector(userSelectors.getInternalUser);

    const [filters, setFilters] = useLocalStorageFilters(
        'PromotersFilters',
        getDefaultFilters(),
        !!internalUser?.impersonatedByUser
    );

    const { data: { promoters, lastPage } = { promoters: [], lastPage: 1 }, isFetching } =
        useGetPromotersQuery(filters);

    const handleSortPromoters = useCallback(
        (property, direction) => {
            setFilters(oldValue => ({ ...oldValue, orderBy: property, direction }));
        },
        [setFilters]
    );

    const handlePageChange = useCallback(
        page => () => {
            setFilters(oldValue => ({ ...oldValue, page }));
        },
        [setFilters]
    );

    const handleEditPromoter = useCallback(
        promoter => {
            dispatch(push(`${url}/edit/${promoter.id}`));
        },
        [dispatch, url]
    );

    return (
        <React.Fragment>
            <Box display="flex" justifyContent="space-between" alignItems="center">
                <Typography variant="h5" align="left" gutterBottom>
                    {texts.PROMOTERS}
                </Typography>
                <ActionButton
                    variant="outlined"
                    sx={listingActionButtonStyle}
                    onClick={() => dispatch(push(`${url}/create`))}
                >
                    {texts.PROMOTER_CREATE}
                </ActionButton>
            </Box>
            <TableContainer sx={{ marginTop: '30px' }}>
                <Table style={{ tableLayout: 'fixed' }}>
                    <colgroup>
                        {tableHeads.map(th => (
                            <col key={th.id} width={`${th.width}%`} />
                        ))}
                    </colgroup>
                    <EnhancedTableHead
                        headCells={tableHeads}
                        orderBy={filters.orderBy}
                        direction={filters.direction}
                        handleSort={handleSortPromoters}
                    />
                    {promoters?.length === 0 || isFetching ? (
                        <EmptyTableBody colSpan={tableHeads.length} loading={isFetching} />
                    ) : (
                        <TableBody>
                            {promoters?.map(promoter => (
                                <PromoterRow
                                    key={promoter.id}
                                    promoter={promoter}
                                    onEdit={handleEditPromoter}
                                />
                            ))}
                        </TableBody>
                    )}
                    <TableFooter>
                        <TableRow>
                            <CustomTablePagination
                                currentPage={filters.page}
                                lastPage={lastPage}
                                changePage={handlePageChange}
                                columns={tableHeads.length}
                            />
                        </TableRow>
                    </TableFooter>
                </Table>
            </TableContainer>
        </React.Fragment>
    );
};

export default PromotersTable;
